import { useState, type ReactNode } from "react";
import { useTranslate } from "@refinedev/core";

import { IgIcoon, KnopEnvelop, KnopPijl, MerkLogo, StapChevron, StapIcoon } from "./advertentieIkZoekBaasIconen";

/**
 * De advertentie zoals hij op ikzoekbaas.nl van de Dierenbescherming komt te staan
 * (maintainer 2026-08-18: "pak een dier op ikzoekbaas.nl en bouw die advertentie exact na").
 *
 * <p>Nagebouwd naar de echte pagina (ikzoekbaas.dierenbescherming.nl/asieldier/honden/…): de balk met het
 * logo en de gele DONEER-knop, het kruimelpad, links de ronde foto die over de naamkaart valt met daaronder
 * de asielkaart en het deelblok, rechts het grote beeld met de tekst en de kenmerkentabel. Kleuren en
 * vormen komen van de site zelf: mint {@code #e7f9f7}, turquoise {@code #00cbbb}, limoen {@code #dfdf00}.
 *
 * <p>Dit blijft een <b>preview</b>: het is een nabootsing van hoe het dier er straks bij staat, gevoed met
 * de gegevens uit deze advertentie. Er wordt niets naar ikzoekbaas gestuurd.
 */

export interface IkZoekBaasData {
  naam: string;
  soort: string | null;
  ras: string | null;
  geslacht: string | null;
  leeftijd: string | null;
  geboortedatum: string | null;
  locatie: string | null;
  adres: string | null;
  /** De beschikbaarheid; op een telefoon staat die als badge naast de naam. */
  beschikbaarheid?: string | null;
  telefoon: string | null;
  email: string | null;
  /** De tekst die de vestiging zelf onder stap 3 heeft gezet (Instellingen → Advertentie). */
  contactTekst: string | null;
  teksten: Array<[string, string]>;
  kenmerken: Array<{ label: string; waarde: string }>;
  /** Openen van de vragenlijst; zonder deze callback is de tabel gewoon tekst. */
  onKenmerken?: () => void;
  /** Openen van het fotobeheer; zet een plusje op het beeld. */
  onFotos?: () => void;
  foto: ReactNode;
  /** Het grote beeld; bij meer dan één advertentiefoto is dit een diavoorstelling. */
  heroFoto?: ReactNode;
  /** Hoeveel foto's de advertentie heeft; de site toont daar "1/9" met pijltjes onder. */
  aantalFotos: number;
  /** Welke foto er nu te zien is (1-gebaseerd), zodat de teller met de diavoorstelling meeloopt. */
  huidigeFoto?: number;
}

/** Eén uitklapbare stap: de kop met hartje en pijl, en de inhoud die eronder verschijnt. */
function Stap({ nr, titel, open, onToggle, children }: {
  nr: number;
  titel: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const dp = titel.indexOf(":");
  return (
    <section className={open ? "izb__stap is-open" : "izb__stap"}>
      <button type="button" className="izb__stap-kop" aria-expanded={open} onClick={onToggle}>
        <StapIcoon nr={nr} />
        <span className="izb__stap-titel">
          <b>{dp === -1 ? titel : titel.slice(0, dp + 1)}</b>
          {dp === -1 ? "" : titel.slice(dp + 1)}
        </span>
        <StapChevron open={open} />
      </button>
      {open && <div className="izb__stap-inhoud" data-stap={nr}>{children}</div>}
    </section>
  );
}

export function AdvertentieIkZoekBaas({ data, device }: {
  data: IkZoekBaasData;
  device: "desktop" | "mobile" | "instagram";
}) {
  const t = useTranslate();
  const naam = data.naam || "—";
  // Alle drie mogen tegelijk open staan: in de preview wil je de hele advertentie in één blik kunnen
  // nalezen, niet stap voor stap opendraaien. Stap 1 staat open bij binnenkomst, daar staat het verhaal.
  const [open, setOpen] = useState<number[]>([1]);
  const wissel = (nr: number) =>
    setOpen((cur) => (cur.includes(nr) ? cur.filter((n) => n !== nr) : [...cur, nr]));

  // De drie stappen staan op elk formaat hetzelfde in de advertentie; alleen wat eromheen zit
  // verschilt per weergave.
  const stappen = (
    <>
      <Stap
        nr={1}
        titel={t("advertentieIkzoekbaas.stap1")}
        open={open.includes(1)}
        onToggle={() => wissel(1)}
      >
        {data.teksten.length === 0 && data.kenmerken.length === 0 && (
          <p className="izb__leegtekst">{t("advertentieIkzoekbaas.geenTekst")}</p>
        )}
        {/* De site zet geen tussenkopjes boven de tekstblokken: het verhaal loopt als één stuk
            door, met de kenmerkentabel eronder. */}
        {data.teksten.map(([kop, tekst]) => (
          <section key={kop} className="izb__tekst">
            {tekst.split(/\n{2,}/).map((alinea, i) => <p key={i}>{alinea}</p>)}
          </section>
        ))}
        {data.kenmerken.length > 0 && (
          <section
            className={data.onKenmerken ? "izb__kenmerken izb__kenmerken--klikbaar" : "izb__kenmerken"}
            onClick={data.onKenmerken}
            role={data.onKenmerken ? "button" : undefined}
            tabIndex={data.onKenmerken ? 0 : undefined}
            title={data.onKenmerken ? t("advertentieIkzoekbaas.kenmerkenInvullen") : undefined}
            onKeyDown={(e) => {
              if (data.onKenmerken && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                data.onKenmerken();
              }
            }}
          >
            <h3>{t("advertentieIkzoekbaas.kenmerken")}</h3>
            <dl>
              {data.kenmerken.map((k) => (
                <div key={k.label} className="izb__kenmerk">
                  <dt>{k.label}</dt>
                  <dd><span aria-hidden="true">:</span>{k.waarde}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}
      </Stap>

      <Stap
        nr={2}
        titel={t("advertentieIkzoekbaas.stap2")}
        open={open.includes(2)}
        onToggle={() => wissel(2)}
      >
        {/* Stap 2 is op de site alleen een verwijzing: de tarieven staan op de tarievenpagina, niet in
            de advertentie. */}
        <span className="izb__knop izb__knop--groot">
          {t("advertentieIkzoekbaas.naarWebsite")}
          <KnopPijl />
        </span>
      </Stap>

      <Stap
        nr={3}
        titel={t("advertentieIkzoekbaas.stap3")}
        open={open.includes(3)}
        onToggle={() => wissel(3)}
      >
        <p>{data.contactTekst || t("advertentieIkzoekbaas.stap3.tekst", { args: [naam] })}</p>
        {(data.locatie || data.adres || data.telefoon || data.email) && (
          <p className="izb__contact">
            {data.locatie && <strong>{data.locatie}</strong>}
            {data.adres && <span>{data.adres}</span>}
            {data.telefoon && <span>{data.telefoon}</span>}
            {data.email && <a className="izb__mail" href={`mailto:${data.email}`}>{data.email}</a>}
          </p>
        )}
        <span className="izb__knop izb__knop--groot">
          <KnopEnvelop />
          {t("advertentieIkzoekbaas.neemContact")}
        </span>
      </Stap>
    </>
  );

  // Een post is geen website: geen menubalk, geen kruimelpad, geen kaarten met knoppen. Wel de vorm van
  // een Instagram-bericht — accountregel, vierkant beeld, de knoppenrij, de likes en dan het bijschrift —
  // want dat is waar de operator naar kijkt als hij zich afvraagt of de foto en de eerste zin werken.
  if (device === "instagram") {
    const bijschrift = data.teksten.map(([, tekst]) => tekst).join("\n\n");
    const eersteRegel = bijschrift.split(/\n{2,}/)[0] ?? "";
    const rest = bijschrift.slice(eersteRegel.length).trim();
    return (
      <div className="izb izb--instagram">
        <header className="izb__ig-kop">
          <span className="izb__ig-avatar" aria-hidden="true">
            <MerkLogo kort />
          </span>
          <span className="izb__ig-account">
            <strong>ikzoekbaas</strong>
            {data.locatie && <small>{data.locatie}</small>}
          </span>
          <span className="izb__ig-meer" aria-hidden="true">···</span>
        </header>

        {/* Ook hier lopen de foto's af: een post met meer beelden laat je in de preview net zo zien. */}
        <div className="izb__post-beeld">{data.heroFoto ?? data.foto}</div>

        <div className="izb__ig-acties" aria-hidden="true">
          <IgIcoon soort="hart" />
          <IgIcoon soort="reactie" />
          <IgIcoon soort="delen" />
          <span className="izb__ig-bewaar"><IgIcoon soort="bewaar" /></span>
        </div>

        <div className="izb__post-tekst">
          <p className="izb__ig-likes" aria-hidden="true">{t("advertentieIkzoekbaas.ig.likes")}</p>
          {bijschrift ? (
            <>
              <p className="izb__ig-bijschrift">
                <strong>ikzoekbaas</strong> {eersteRegel}
              </p>
              {rest && <p className="izb__ig-meerTekst">{t("advertentieIkzoekbaas.ig.meer")}</p>}
            </>
          ) : (
            <p className="izb__leegtekst">{t("advertentieIkzoekbaas.geenTekst")}</p>
          )}
          <p className="izb__post-tags">
            #ikzoekbaas #adopteernietkopen {data.soort ? "#" + data.soort.toLowerCase() : ""}
            {data.naam ? " #" + data.naam.toLowerCase().replace(/\s+/g, "") : ""}
          </p>
          <p className="izb__ig-tijd" aria-hidden="true">{t("advertentieIkzoekbaas.ig.tijd")}</p>
        </div>
      </div>
    );
  }

  if (device === "mobile") {
    return (
      <div className="izb izb--mobile">
        <header className="izb__mnav">
          <MerkLogo kort />
          <span className="izb__mdoneer" aria-hidden="true">
            {t("advertentieIkzoekbaas.doneer")} ♡
          </span>
          <span className="izb__mrond" aria-hidden="true">☺</span>
          <span className="izb__mmenu" aria-hidden="true">☰</span>
        </header>

        <p className="izb__mterug" aria-hidden="true">
          ← <span>{data.soort ?? t("advertentieIkzoekbaas.dieren")}</span>
        </p>

        <div className="izb__mkop">
          <span className="izb__mfoto">{data.foto}</span>
          <span className="izb__mkop-tekst">
            {data.beschikbaarheid && <span className="izb__mbadge">{data.beschikbaarheid}</span>}
            <h2 className="izb__mnaam">{naam}</h2>
          </span>
        </div>

        {data.geboortedatum && (
          <p className="izb__mgeboorte">
            <span aria-hidden="true">🎂</span> {data.geboortedatum}
          </p>
        )}

        <div className="izb__mbeeld">
          <div className="izb__hero">
            {data.heroFoto ?? data.foto}
            {data.onFotos && (
              <button type="button" className="izb__foto-plus" onClick={data.onFotos}
                title={t("advertentieIkzoekbaas.fotos")} aria-label={t("advertentieIkzoekbaas.fotos")}>
                +
              </button>
            )}
          </div>
          {data.aantalFotos > 1 && (
            <p className="izb__teller" aria-hidden="true">
              <span>←</span> {`${data.huidigeFoto ?? 1}/${data.aantalFotos}`} <span>→</span>
            </p>
          )}
        </div>

        <div className="izb__stappen">{stappen}</div>

        <div className="izb__mkaarten">
          {(data.locatie || data.adres) && (
            <div className="izb__kaart">
              <h3>{data.locatie ?? ""}</h3>
              {data.adres && <p className="izb__adres">{data.adres}</p>}
              <span className="izb__knop">{t("advertentieIkzoekbaas.andereDieren")}</span>
            </div>
          )}
          <div className="izb__kaart">
            <h3>{t("advertentieIkzoekbaas.deelProfiel", { args: [naam] })}</h3>
            <p>{t("advertentieIkzoekbaas.deelTekst")}</p>
            <span className="izb__socials" aria-hidden="true">
              <i>f</i><i>𝕏</i><i>✉</i><i>☏</i>
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`izb izb--${device}`}>
      <header className="izb__nav">
        <MerkLogo />
        <nav className="izb__menu" aria-hidden="true">
          <span>ZOEK EEN DIER</span>
          <span>TIPS</span>
          <span>OVER ONS</span>
          <span>ACCOUNT</span>
        </nav>
        <span className="izb__nav-acties" aria-hidden="true">
          <span className="izb__doneer">DONEER ♥</span>
          <span className="izb__inloggen">INLOGGEN</span>
        </span>
      </header>

      <div className="izb__kruimels" aria-hidden="true">
        <span>Home</span> / <span>{data.soort ?? t("advertentieIkzoekbaas.dieren")}</span> /{" "}
        <strong>{naam}</strong>
      </div>

      <div className="izb__body">
        <aside className="izb__zij">
          <div className="izb__profiel">
            <span className="izb__rondfoto">{data.foto}</span>
            <h2 className="izb__naam">{naam}</h2>
            {data.geboortedatum && (
              <p className="izb__geboorte">
                <span aria-hidden="true">🎂</span> {data.geboortedatum}
              </p>
            )}
          </div>

          {(data.locatie || data.adres) && (
            <div className="izb__kaart">
              <h3>{data.locatie ?? ""}</h3>
              {data.adres && <p className="izb__adres">{data.adres}</p>}
              <span className="izb__knop">{t("advertentieIkzoekbaas.andereDieren")}</span>
            </div>
          )}

          <div className="izb__kaart">
            <h3>{t("advertentieIkzoekbaas.deelProfiel", { args: [naam] })}</h3>
            <p>{t("advertentieIkzoekbaas.deelTekst")}</p>
            <span className="izb__socials" aria-hidden="true">
              <i>f</i><i>𝕏</i><i>✉</i><i>☏</i>
            </span>
          </div>
        </aside>

        <main className="izb__hoofd">
          {/* De foto zit in een kaart met een mintvlak dat er net achter uitsteekt — dat is hoe de site
              het beeld presenteert — met eronder de fototeller. */}
          <div className="izb__beeld">
            <div className="izb__hero">
              {data.heroFoto ?? data.foto}
              {data.onFotos && (
                /* De foto's beheer je vanaf de foto zelf; een los blok onder de advertentie hoeft niet. */
                <button type="button" className="izb__foto-plus" onClick={data.onFotos}
                  title={t("advertentieIkzoekbaas.fotos")} aria-label={t("advertentieIkzoekbaas.fotos")}>
                  +
                </button>
              )}
            </div>
            {data.aantalFotos > 1 && (
              <p className="izb__teller" aria-hidden="true">
                <span>←</span> {`${data.huidigeFoto ?? 1}/${data.aantalFotos}`} <span>→</span>
              </p>
            )}
          </div>

          {/* De drie stappen zijn op de site uitklappers, en de eerste draagt het verhaal van het dier
              met daaronder de kenmerkentabel. Open je hem dicht, dan zie je precies wat een bezoeker
              ziet voordat hij klikt. */}
          <div className="izb__stappen">{stappen}</div>
        </main>
      </div>
    </div>
  );
}
